import { Icon, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { MoreInformationDialogContent } from '~/app/pages/configurator/components/more-information-dialog/components/more-information-dialog-content';
import {
	MoreInformationDialogHeaderStyles,
	StyledButton,
} from '~/app/pages/configurator/components/more-information-dialog/styles/more-information-dialog-style';
import { showMoreInformationDialog } from '~/app/pages/configurator/models/show-more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { Dialog } from '~/components/dialog';
import { RenderHtml } from '~/components/text';
import { ConfiguredCarFeatureFragment, ConfiguredCarPackageFeatureFragment } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItem } from '~/graphql/mapper';
import { ContentOrPackage, FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { state } from '~/storage';
import { expect } from '~/utils/unwrap';

type MoreInformationDialogContentProps = React.ComponentProps<typeof MoreInformationDialogContent>;

export function MoreInformation({
	item,
	featureOrPackage,
	hideAddToConfigurationButton,
	hidePriceAndAvailability,
	onOpen,
	triggerText,
}: {
	item?: CatalogItem | undefined;
	featureOrPackage: MoreInformationDialogContentProps['featureOrPackage'];
	hideAddToConfigurationButton?: boolean;
	/** Workaround for NWM-150 (price and availability not available for package content) */
	hidePriceAndAvailability?: boolean;
	onOpen?: (open: boolean) => void;
	triggerText?: string;
}) {
	const { i18n } = useI18n();
	const [open, setOpen] = useState(false);
	const { changeConfiguration } = useConfigurator();
	const [subItem, setSubItem] = useState<ConfiguredCarFeatureFragment | ConfiguredCarPackageFeatureFragment | null>(
		null,
	);
	const { getName } = useDebugMode();
	const showMoreInformation = showMoreInformationDialog(featureOrPackage as FeatureOrPackage);
	const getDebugItem = (itemToMap: ContentOrPackage | undefined): { name: string; pr7: string } | undefined => {
		if (!itemToMap) return;
		if (itemToMap.__typename === 'ConfiguredCarPackageFeature') {
			return { ...itemToMap, pr7: itemToMap.contentId };
		}
		return { name: itemToMap.name || '', pr7: itemToMap.pr7 };
	};

	const onAddToConfiguration = hideAddToConfigurationButton
		? undefined
		: () => {
				setOpen(false);

				if (item) {
					changeConfiguration(item);
				} else {
					console.error('MoreInformationDialogContent requires item prop to be defined');
				}
			};

	const onOpenChange = useCallback(
		(isOpen: boolean) => {
			if (onOpen) {
				onOpen(isOpen);
			}
			setOpen(isOpen);
			setSubItem(null);

			!isOpen &&
				appTrackingService.viewChangeEvent({
					eventName: 'generic',
				});
		},
		[onOpen],
	);

	useEffect(() => {
		open &&
			appTrackingService.viewChangeEvent({
				eventName: 'configurator - details layer',
				componentName: 'details-layer',
				item: featureOrPackage as FeatureOrPackage,
			});
	}, [JSON.stringify(featureOrPackage), open]);

	const breadCrumbRef = useRef<HTMLSpanElement | null>(null);
	useEffect(() => {
		if (subItem) breadCrumbRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [subItem]);

	return showMoreInformation ? (
		<Dialog
			open={open}
			onCloseButtonClick={() => onOpenChange(false)}
			triggerElement={
				<TextLink variant={'standalone'} size={'sm'} onClick={() => onOpenChange(true)}>
					{triggerText || i18n('ngc.actions.moreInfo')}
				</TextLink>
			}>
			{subItem ? (
				<>
					<MoreInformationDialogHeaderStyles
						ref={(x) => {
							breadCrumbRef.current = x;
						}}
						className="flex items-center">
						<StyledButton onClick={() => setSubItem(null)}>
							<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
								<RenderHtml html={getName(getDebugItem(featureOrPackage))} />
								<Icon className="flex items-center justify-center" name={'forward'} size="s" />
							</Text>
						</StyledButton>
						<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
							<RenderHtml html={subItem.name} />
						</Text>
					</MoreInformationDialogHeaderStyles>
					<MoreInformationDialogContent
						featureOrPackage={subItem}
						onAddToConfiguration={undefined}
						hidePriceAndAvailability
					/>
				</>
			) : (
				<MoreInformationDialogContent
					setSubItem={setSubItem}
					featureOrPackage={expect(
						featureOrPackage,
						"MoreInformationDialogContent requires 'featureOrPackage' prop to be defined",
					)}
					onAddToConfiguration={onAddToConfiguration}
					hidePriceAndAvailability={hidePriceAndAvailability}
				/>
			)}
		</Dialog>
	) : (
		<></>
	);
}

export const useMoreInformationDialog = () => {
	const [dialogInformation, setDialogInformation] = useAtom(state.moreInformationDialogData);

	const openMoreInformationDialog = (
		item: SelectableOption<FeatureCategory>,
		featureOrPackage?: ContentOrPackage,
		trackSelection?: () => void,
		step?: number,
		total?: number,
		category?: string,
	) => {
		if (!featureOrPackage) return;

		setDialogInformation({
			open: true,
			item,
			featureOrPackage,
			hideAddConfigButton: false,
			hidePriceAndAvailability: false,
			trackSelection,
			step,
			total,
			category,
		});
	};

	const closeMoreInformationDialog = () =>
		setDialogInformation({
			open: false,
			item: undefined,
			featureOrPackage: undefined,
			hideAddConfigButton: false,
			hidePriceAndAvailability: false,
		});
	return {
		openMoreInformationDialog,
		closeMoreInformationDialog,
		open: dialogInformation?.open || false,
		item: dialogInformation?.item,
		featureOrPackage: dialogInformation?.featureOrPackage,
		hideAddToConfigurationButton: dialogInformation?.hideAddConfigButton || false,
		hidePriceAndAvailability: dialogInformation?.hidePriceAndAvailability || false,
		trackSelection: dialogInformation?.trackSelection,
		step: dialogInformation?.step,
		total: dialogInformation?.total,
		category: dialogInformation?.category,
	};
};

export const MoreInformationDialog = () => {
	const {
		closeMoreInformationDialog,
		open,
		item,
		featureOrPackage,
		hideAddToConfigurationButton,
		hidePriceAndAvailability,
		trackSelection,
		step,
		total,
		category,
	} = useMoreInformationDialog();
	const { getName } = useDebugMode();
	const { changeConfiguration } = useConfigurator();
	const [subItem, setSubItem] = useState<ConfiguredCarFeatureFragment | ConfiguredCarPackageFeatureFragment | null>(
		null,
	);
	const { i18n } = useI18n();

	const trackOpenClose = useCallback(
		(action: 'open' | 'close') => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `click to ${action} details layer`,
				label: i18n('ngc.actions.moreInfo'),
				elementName: 'text link',
				value: 'more information',
				category,
				elementCurrentStep: step ?? 0,
				elementTotalSteps: total ?? 0,
				item: featureOrPackage as FeatureOrPackage,
			});

			appTrackingService.viewChangeEvent({
				eventName: action === 'open' ? 'configurator - details layer' : 'generic',
				componentName: action === 'open' ? 'details-layer' : '',
				item: action === 'open' ? (featureOrPackage as FeatureOrPackage) : undefined,
			});
		},
		[category, JSON.stringify(featureOrPackage), i18n, step, total],
	);

	useEffect(() => {
		if (open) {
			trackOpenClose('open');
		}
	}, [category, JSON.stringify(featureOrPackage), i18n, open, step, total, trackOpenClose]);

	const getDebugItem = (itemToMap: ContentOrPackage | undefined): { name: string; pr7: string } | undefined => {
		if (!itemToMap) return;
		if (itemToMap.__typename === 'ConfiguredCarPackageFeature') {
			return { ...itemToMap, pr7: itemToMap.contentId };
		}
		return { name: itemToMap.name || '', pr7: itemToMap.pr7 };
	};

	const onAddToConfiguration = hideAddToConfigurationButton
		? undefined
		: () => {
				closeMoreInformationDialog();

				if (item) {
					trackSelection && trackSelection();
					changeConfiguration(item);
				} else {
					console.error('MoreInformationDialogContent requires item prop to be defined');
				}
			};

	const onClose = useCallback(() => {
		closeMoreInformationDialog();
		setSubItem(null);
		trackOpenClose('close');
	}, [closeMoreInformationDialog]);

	const breadCrumbRef = useRef<HTMLSpanElement | null>(null);
	useEffect(() => {
		if (subItem) breadCrumbRef.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
	}, [subItem]);

	if (!featureOrPackage) return <></>;
	return (
		<Dialog onCloseButtonClick={onClose} open={open}>
			{subItem ? (
				<>
					<span
						ref={(x) => {
							breadCrumbRef.current = x;
						}}
						className="flex items-center">
						<button onClick={() => setSubItem(null)}>
							<Text className="flex" variant={{ type: 'copy', order: '1', style: 'normal' }}>
								<RenderHtml html={getName(getDebugItem(featureOrPackage))} />
								<Icon className="flex items-center justify-center" name={'forward'} size="s" />
							</Text>
						</button>
						<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
							<RenderHtml html={subItem.name} />
						</Text>
					</span>
					<MoreInformationDialogContent
						featureOrPackage={subItem}
						onAddToConfiguration={undefined}
						hidePriceAndAvailability
					/>
				</>
			) : (
				<MoreInformationDialogContent
					setSubItem={setSubItem}
					featureOrPackage={expect(
						featureOrPackage,
						"MoreInformationDialogContent requires 'featureOrPackage' prop to be defined",
					)}
					onAddToConfiguration={onAddToConfiguration}
					hidePriceAndAvailability={hidePriceAndAvailability}
				/>
			)}
		</Dialog>
	);
};
